import { Link } from "react-router-dom";
import type { Article } from "@/mocks/types";

interface ArticleCardProps {
  article: Article;
  layout?: "vertical" | "horizontal";
  showDeck?: boolean;
}

export default function ArticleCard({
  article,
  layout = "vertical",
  showDeck = true,
}: ArticleCardProps) {
  if (layout === "horizontal") {
    return (
      <article className="group flex flex-col gap-4 sm:flex-row sm:items-start">
        <Link
          to={`/article/${article.id}`}
          className="block w-full shrink-0 overflow-hidden rounded-lg sm:w-[280px]"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={article.alt}
            className="h-44 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 sm:h-40"
            loading="lazy"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
              {article.categoryName}
            </span>
            <span className="text-xs text-foreground-400">{article.publishDate}</span>
          </div>
          <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950">
            <Link
              to={`/article/${article.id}`}
              className="line-clamp-2 transition-colors group-hover:text-primary-600"
            >
              {article.title}
            </Link>
          </h3>
          {showDeck && (
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">
              {article.deck}
            </p>
          )}
          <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-[11px] font-medium text-primary-700">
                {article.authorInitial}
              </span>
              {article.authorName}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readMinutes} 分钟
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
      <Link
        to={`/article/${article.id}`}
        className="block overflow-hidden"
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={article.alt}
          className="h-44 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
            {article.categoryName}
          </span>
          {article.breaking && (
            <span className="inline-flex items-center gap-1 rounded bg-primary-100 px-2 py-0.5 text-xs text-primary-700">
              <i className="ri-flashlight-line"></i>独家
            </span>
          )}
        </div>
        <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 transition-colors group-hover:text-primary-600"
          >
            {article.title}
          </Link>
        </h3>
        {showDeck && (
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">
            {article.deck}
          </p>
        )}
        <div className="mt-auto flex items-center gap-3 pt-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-[11px] font-medium text-primary-700">
              {article.authorInitial}
            </span>
            {article.authorName}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.readMinutes} 分钟
          </span>
          <span className="ml-auto inline-flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views}
          </span>
        </div>
      </div>
    </article>
  );
}